import type { AcpSubagentData } from '@crowdex/agent-contracts';
import { Button } from '@crowdex/ui/components/button';
import { cn } from '@crowdex/ui/lib/utils';
import { Bot, Check, ChevronRight, CircleSlash, LoaderCircle, XCircle } from 'lucide-react';
import { useContext } from 'react';
import { InterruptedActionContext } from '@/lib/conversations/message-part/interrupted-action-context';
import { subagentPreview } from '@/lib/conversations/message-part/subagent';
import { useConversationOpener } from '@/lib/desktop-shell/conversation-opener';
import { TRANSCRIPT_CARD } from './command-card';

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** "3 subagents · 2 done", "1 subagent · running". */
function subagentsTitle(subagents: readonly AcpSubagentData[]): string {
  const count = subagents.length;
  const done = subagents.filter((data) => data.status === 'completed').length;
  const head = `${count} ${count === 1 ? 'subagent' : 'subagents'}`;
  if (done === count) return `${head} · done`;
  if (count === 1)
    return `${head} · ${subagentPreview(subagents[0] as AcpSubagentData).running ? 'running' : 'stopped'}`;
  return `${head} · ${done} done`;
}

function SubagentRow({
  data,
  onOpen,
}: {
  data: AcpSubagentData;
  onOpen?: (toolCallId: string) => void;
}) {
  const { failed, interrupted, running } = subagentPreview(data);
  const Icon = running ? LoaderCircle : interrupted ? CircleSlash : failed ? XCircle : Check;
  const resume = useContext(InterruptedActionContext);
  const canResume = interrupted && resume !== null && resume.activeToolCallId === data.toolCallId;
  const resumeAction = {
    kind: 'subagent' as const,
    toolCallId: data.toolCallId,
    toolName: data.title,
    title: data.title,
    input: { prompt: data.prompt, agentType: data.agentType },
  };
  // A Crowdex subagent is a conversation of its own (D49): it opens there, with its composer;
  // another agent's subagent shows in the panel.
  const opener = useConversationOpener();
  const childId = data.threadId !== null && UUID.test(data.threadId) ? data.threadId : null;
  const open = async () => {
    if (childId !== null && opener !== null && (await opener.openConversation(childId))) return;
    onOpen?.(data.toolCallId);
  };
  return (
    <li>
      <button
        className="flex h-9 w-full items-center gap-2.5 rounded-xl px-2 text-left text-foreground text-sm transition-colors hover:bg-control/60 disabled:hover:bg-transparent"
        disabled={onOpen === undefined && childId === null}
        onClick={() => void open()}
        type="button"
      >
        <Icon
          aria-label={
            running ? 'Running' : interrupted ? 'Interrupted' : failed ? 'Failed' : 'Done'
          }
          className={cn(
            'size-3.5 shrink-0',
            running
              ? 'animate-spin text-running'
              : failed
                ? 'text-destructive'
                : interrupted
                  ? 'text-muted-foreground'
                  : 'text-success',
          )}
        />
        <span className="min-w-0 flex-1 truncate">{data.title}</span>
        {onOpen !== undefined && (
          <ChevronRight aria-hidden className="size-4 shrink-0 text-muted-foreground" />
        )}
      </button>
      {canResume && resume !== null && (
        <div className="flex flex-wrap items-center gap-2 px-2 pb-1.5 pl-8">
          <span className="text-muted-foreground text-xs">Interrupted.</span>
          <Button onClick={() => resume.onResume(resumeAction)} size="xs">
            Run it again
          </Button>
          <Button onClick={() => resume.onSkip(resumeAction)} size="xs" variant="ghost">
            Skip
          </Button>
        </div>
      )}
    </li>
  );
}

/**
 * The subagents a turn started, in one card (design D11): "3 subagents · 2 done", then one row
 * each, whose one action opens it as a child conversation.
 */
export function SubagentsCard({
  subagents,
  onOpen,
}: {
  subagents: readonly AcpSubagentData[];
  onOpen?: (toolCallId: string) => void;
}) {
  if (subagents.length === 0) return null;
  return (
    <section
      aria-label={subagentsTitle(subagents)}
      className={cn(TRANSCRIPT_CARD, 'px-1.5 pt-2.5 pb-1.5')}
      data-subagents-card
    >
      <header className="type-meta flex items-center gap-2 px-2 pb-1 text-muted-foreground">
        <Bot aria-hidden className="size-3.5" />
        {subagentsTitle(subagents)}
      </header>
      <ul>
        {subagents.map((data) => (
          <SubagentRow data={data} key={data.toolCallId} onOpen={onOpen} />
        ))}
      </ul>
    </section>
  );
}

/** One subagent, as a card of its own. */
export function SubagentView({ data, onOpen }: { data: AcpSubagentData; onOpen?: () => void }) {
  return (
    <SubagentsCard onOpen={onOpen === undefined ? undefined : () => onOpen()} subagents={[data]} />
  );
}
